import type { AppData, DashboardSettings, FollowupQuestion, HdmPost, LearnedBelief, Report, Source, TenantMember, Trend } from '../types'
import { demoData } from './demo'
import { supabase, tenantId } from './supabase'

const requireCloud = () => {
  if (!supabase) throw new Error('Permanent memory is not configured yet.')
  return supabase
}

const trendFromRow = (row: Record<string, any>, evidence: Trend['evidence'] = []): Trend => {
  const saved = row.trend_json ?? {}
  const score = Number(row.emergence_score ?? saved.emergence_score ?? 0)
  return {
    id: row.id, name: row.name, trendClass: row.trend_class,
    geography: row.geography === 'GLOBAL' ? 'GLOBAL' : 'UAE',
    lifecycle: row.lifecycle, route: row.route, emergence: score,
    relevance: Number(row.relevance_score ?? 0), confidence: Number(row.confidence ?? 0),
    independentCreators: Number(saved.independent_creators ?? 0), platforms: saved.platforms ?? [],
    reasons: saved.reasons ?? [], uncertainty: saved.uncertainty ?? [],
    firstSeen: row.first_seen_at, lastSeen: row.last_seen_at,
    evidenceCount: saved.evidence_ids?.length ?? 0,
    evidence,
    aiSummary: saved.ai_summary ?? undefined,
    hdmIdeas: saved.hdm_ideas ?? [],
    sparkline: [Math.max(0, score - 18), Math.max(0, score - 12), Math.max(0, score - 8), Math.max(0, score - 3), score],
  }
}

const sourceFromRow = (row: Record<string, any>): Source => ({
  id: row.id, username: row.username, platform: row.platform,
  panel: row.panel.replaceAll('_', ' '), status: row.status,
  leadTimeHours: row.lead_time_hours == null ? null : Number(row.lead_time_hours),
  usefulDiscoveries: Number(row.useful_discoveries ?? 0),
  repostRatio: Number(row.repost_ratio ?? 0), costUsd: Number(row.cost_usd ?? 0),
})

export async function loadCloudData(): Promise<AppData> {
  const client = requireCloud()
  const freshnessCutoff = new Date(Date.now() - 36 * 60 * 60 * 1000).toISOString()
  const results = await Promise.all([
    client.from('trends').select('*').eq('tenant_id', tenantId).is('archived_at', null).gte('last_seen_at', freshnessCutoff).order('emergence_score', { ascending: false }),
    client.from('sources').select('*').eq('tenant_id', tenantId).order('panel'),
    client.from('learned_beliefs').select('*').eq('tenant_id', tenantId).order('created_at', { ascending: false }),
    client.from('reports').select('*').eq('tenant_id', tenantId).order('created_at', { ascending: false }).limit(30),
    client.from('settings_versions').select('*').eq('tenant_id', tenantId).eq('status', 'active').order('version', { ascending: false }).limit(1).maybeSingle(),
    client.from('runs').select('*').eq('tenant_id', tenantId).order('started_at', { ascending: false }).limit(1).maybeSingle(),
    client.from('evidence').select('id', { count: 'exact', head: true }).eq('tenant_id', tenantId),
    client.from('integration_health').select('*').eq('tenant_id', tenantId).order('name'),
    client.from('decisions').select('id,trend_id,created_at,reason_text,trends(name)').eq('tenant_id', tenantId).eq('decision', 'pursue').is('reason_text', null),
    client.from('hdm_trend_matches').select('trend_id').eq('tenant_id', tenantId),
    client.from('evidence').select('id,url,published_at,normalized_json').eq('tenant_id', tenantId).eq('source_panel', 'hdm_owned').order('published_at', { ascending: false }).limit(50),
    client.from('predictions').select('outcome').eq('tenant_id', tenantId).not('evaluated_at', 'is', null),
    client.from('trend_evidence').select('trend_id,evidence:evidence_id(id,url,creator_username,published_at,normalized_json)').limit(500),
  ])
  const firstError = results.find((result) => result.error)?.error
  if (firstError) throw firstError
  const [trendsResult, sourcesResult, beliefsResult, reportsResult, settingsResult, runResult, evidenceResult, healthResult, decisionsResult, matchesResult, hdmPostsResult, predictionsResult, trendEvidenceResult] = results
  const evidenceByTrend = new Map<string, NonNullable<Trend['evidence']>>()
  for (const row of trendEvidenceResult.data ?? []) {
    const item: any = row.evidence
    if (!item) continue
    const current = evidenceByTrend.get(row.trend_id) ?? []
    current.push({
      id: item.id,
      url: item.url,
      creatorUsername: item.creator_username,
      caption: String(item.normalized_json?.caption ?? '').slice(0, 180),
      audioTitle: item.normalized_json?.audio_title ?? undefined,
      publishedAt: item.published_at,
    })
    evidenceByTrend.set(row.trend_id, current)
  }
  for (const items of evidenceByTrend.values()) {
    items.sort((left, right) => new Date(right.publishedAt ?? 0).getTime() - new Date(left.publishedAt ?? 0).getTime())
  }
  const beliefs: LearnedBelief[] = (beliefsResult.data ?? []).map((row: any) => ({
    id: row.id, claim: row.claim, confidence: Number(row.confidence),
    evidenceCount: row.evidence_json?.evidence_ids?.length ?? 0,
    effect: row.scoring_effect_json?.summary ?? 'Used as a transparent scoring hint', status: row.status,
  }))
  const reports: Report[] = (reportsResult.data ?? []).map((row: any) => ({
    id: row.id, type: row.report_type, title: row.title, createdAt: row.created_at,
    summary: row.summary, trendCount: row.report_json?.trends?.length ?? row.report_json?.trend_count ?? 0,
    markdown: row.markdown, reportJson: row.report_json,
  }))
  const settings = (settingsResult.data?.settings_json as DashboardSettings | undefined) ?? demoData.settings
  const matchedTrendIds = new Set((matchesResult.data ?? []).map((row: any) => row.trend_id))
  const dueBefore = Date.now() - settings.acceptedTrendFollowupDays * 24 * 60 * 60 * 1000
  const questions: FollowupQuestion[] = (decisionsResult.data ?? [])
    .filter((row: any) => new Date(row.created_at).getTime() <= dueBefore && !matchedTrendIds.has(row.trend_id))
    .map((row: any) => ({ decisionId: row.id, trendId: row.trend_id, trendName: row.trends?.name ?? 'Accepted trend', decidedAt: row.created_at }))
  const hdmPosts: HdmPost[] = (hdmPostsResult.data ?? []).map((row: any) => ({
    evidenceId: row.id, url: row.url, publishedAt: row.published_at,
    caption: String(row.normalized_json?.caption ?? '').slice(0, 100),
  }))
  const evaluatedPredictions = predictionsResult.data ?? []
  const predictionAccuracy = evaluatedPredictions.length
    ? Math.round(evaluatedPredictions.filter((row: any) => row.outcome === 'hit').length / evaluatedPredictions.length * 100)
    : null
  return {
    ...demoData, trends: (trendsResult.data ?? []).map((row: any) => trendFromRow(row, evidenceByTrend.get(row.id) ?? [])),
    sources: (sourcesResult.data ?? []).map(sourceFromRow), beliefs, reports, settings,
    evidenceCount: evidenceResult.count ?? 0, lastScan: runResult.data?.started_at ?? null,
    health: (healthResult.data?.length ? healthResult.data.map((row: any) => ({
      name: row.name, label: row.name.replaceAll('_', ' '), status: row.status,
      message: row.public_message, action: row.owner_action ?? undefined, lastChecked: row.last_checked_at,
    })) : demoData.health),
    questions,
    hdmPosts,
    predictionAccuracy,
  }
}

export async function getCurrentMembership(): Promise<TenantMember> {
  const client = requireCloud()
  const { data: auth, error: authError } = await client.auth.getUser()
  const email = auth.user?.email?.toLowerCase()
  if (authError || !email) throw new Error('Sign in again to verify dashboard access.')
  const { data, error } = await client
    .from('tenant_members')
    .select('email,role,status,created_at,updated_at')
    .eq('tenant_id', tenantId)
    .eq('email', email)
    .maybeSingle()
  if (error) throw error
  if (!data || data.status !== 'active') throw new Error('This email does not currently have dashboard access.')
  return {
    email: data.email,
    role: data.role,
    status: data.status,
    createdAt: data.created_at,
    updatedAt: data.updated_at,
  }
}

export async function loadViewerData(): Promise<AppData> {
  const { data, error } = await requireCloud()
    .from('reports')
    .select('*')
    .eq('tenant_id', tenantId)
    .order('created_at', { ascending: false })
    .limit(30)
  if (error) throw error
  const reports: Report[] = (data ?? []).map((row: any) => ({
    id: row.id,
    type: row.report_type,
    title: row.title,
    createdAt: row.created_at,
    summary: row.summary,
    trendCount: row.report_json?.trends?.length ?? row.report_json?.trend_count ?? 0,
    markdown: row.markdown,
    reportJson: row.report_json,
  }))
  return { ...demoData, reports }
}

export async function saveDecision(trendId: string, decision: string, email: string): Promise<void> {
  const { error } = await requireCloud().from('decisions').insert({
    tenant_id: tenantId, trend_id: trendId, user_email: email.toLowerCase(),
    decision: decision.toLowerCase().replaceAll(' ', '_'),
  })
  if (error) throw error
}

export async function claimInvitation(): Promise<void> {
  const { error } = await requireCloud().rpc('claim_invitation')
  if (error) throw error
}

export async function saveSettings(settings: DashboardSettings): Promise<number> {
  const { data, error } = await requireCloud().rpc('save_settings', {
    new_settings: settings,
    change_summary: 'Dashboard owner update',
  })
  if (error) throw error
  return Number(data)
}

export async function proposeSource(source: Source): Promise<void> {
  const { error } = await requireCloud().from('sources').insert({
    id: source.id, tenant_id: tenantId, platform: source.platform.toLowerCase(),
    username: source.username.toLowerCase(), url: `https://www.instagram.com/${source.username}/`,
    panel: 'carrier', status: 'proposed', added_by: 'dashboard_owner',
  })
  if (error) throw error
}

export async function setSourceStatus(sourceId: string, status: Source['status']): Promise<void> {
  const { error } = await requireCloud().from('sources').update({ status }).eq('id', sourceId).eq('tenant_id', tenantId)
  if (error) throw error
}

export async function answerFollowup(decisionId: string, answer: string): Promise<void> {
  const { error } = await requireCloud().rpc('answer_followup', { decision_uuid: decisionId, answer })
  if (error) throw error
}

export async function submitInsights(evidenceId: string, metrics: Record<string, number | string>): Promise<void> {
  const client = requireCloud()
  const { data: auth } = await client.auth.getUser()
  const { error } = await client.from('performance_observations').insert({
    tenant_id: tenantId, evidence_id: evidenceId, source: 'dashboard_guided_form',
    metrics_json: metrics, private: true, submitted_by: auth.user?.email ?? null,
  })
  if (error) throw error
}

export async function saveWeeklyContext(title: string, content: string, activeDays: number): Promise<void> {
  const { error } = await requireCloud().rpc('save_weekly_context', {
    context_title: title, context_content: content, active_days: activeDays,
  })
  if (error) throw error
}

export async function inviteViewer(email: string): Promise<void> {
  const { data, error } = await requireCloud().functions.invoke('invite-user', {
    body: { action: 'invite', email, tenantId, role: 'viewer' },
  })
  if (error) throw error
  if (data?.error) throw new Error(data.detail ? `${data.error} ${data.detail}` : data.error)
}

export async function listMembers(): Promise<TenantMember[]> {
  const { data, error } = await requireCloud().functions.invoke('invite-user', {
    body: { action: 'list', tenantId },
  })
  if (error) throw error
  if (data?.error) throw new Error(data.error)
  return (data?.members ?? []).map((member: any) => ({
    email: member.email,
    role: member.role,
    status: member.status,
    createdAt: member.created_at,
    updatedAt: member.updated_at,
  }))
}

export async function revokeMember(email: string): Promise<void> {
  const { data, error } = await requireCloud().functions.invoke('invite-user', {
    body: { action: 'revoke', email, tenantId },
  })
  if (error) throw error
  if (data?.error) throw new Error(data.error)
}
